import sortBy from 'lodash/sortBy';

import { computeRecipeChannelPerformance } from './computeRecipeChannelPerformance';

const getSelectionDriver = (lastChangedFilter, selectedVariants, selectedStores) => {
  const hasVariants = selectedVariants.length > 0;
  const hasStores = selectedStores.length > 0;

  if (!hasVariants && !hasStores) {
    return null;
  }

  if (!hasVariants) {
    return 'store';
  }

  if (!hasStores) {
    return 'variant';
  }

  if (lastChangedFilter === 'store') {
    return 'store';
  }

  return 'variant';
};

const buildVariantMaps = ({
  recipeVariants,
  compositions,
  mainRecipeOptionId,
  mainRecipeName,
  allStoreIds,
}) => {
  const variantIndex = new Map();
  const variantByStoreId = new Map();

  // Process specific variants first so we know which stores they claim. The main variant then
  // owns only the leftover stores — otherwise its `storeIds` would include every store, even
  // those assigned to a specific variant, and the variant-driver row would overreport them.
  const claimedStoreIds = new Set();

  recipeVariants.forEach((variant) => {
    // Variants expose their own composition under `variant.composition`; fall back to the main
    // recipe's compositions when a variant has none so the bars still render meaningful values.
    const variantCompositions = variant.composition?.length ? variant.composition : compositions;

    const entry = {
      id: variant.id,
      name: variant.name,
      compositions: variantCompositions,
      storeIds: Array.isArray(variant.storeIds) ? variant.storeIds : [],
    };

    variantIndex.set(variant.id, entry);

    entry.storeIds.forEach((storeId) => {
      variantByStoreId.set(storeId, entry);
      claimedStoreIds.add(storeId);
    });
  });

  const mainVariant = {
    id: mainRecipeOptionId,
    name: mainRecipeName,
    compositions,
    storeIds: allStoreIds.filter((storeId) => !claimedStoreIds.has(storeId)),
  };

  variantIndex.set(mainRecipeOptionId, mainVariant);

  mainVariant.storeIds.forEach((storeId) => {
    variantByStoreId.set(storeId, mainVariant);
  });

  return { variantIndex, variantByStoreId };
};

export const buildPerformanceRows = ({
  lastChangedFilter,
  selectedVariants,
  selectedStores,
  recipeVariants,
  compositions,
  stores,
  recipe,
  associatedProduct,
  channels,
  defaultChannelId,
  mainRecipeOptionId,
  mainRecipeName,
}) => {
  const driver = getSelectionDriver(lastChangedFilter, selectedVariants, selectedStores);

  if (!driver) {
    return [];
  }

  const storesById = new Map(stores.map((store) => [store.id, store]));
  const allStoreIds = stores.map(({ id }) => id);
  const { variantIndex, variantByStoreId } = buildVariantMaps({
    recipeVariants,
    compositions,
    mainRecipeOptionId,
    mainRecipeName,
    allStoreIds,
  });

  const metricsParams = {
    recipe,
    associatedProduct,
    channels,
    defaultChannelId,
  };

  const toRow = (variant, storeNames) => ({
    id: variant.id,
    name: variant.name,
    variantName: variant.name,
    storeNames: sortBy(storeNames),
    byChannel: computeRecipeChannelPerformance({
      compositions: variant.compositions,
      ...metricsParams,
    }),
  });

  if (driver === 'variant') {
    return selectedVariants
      .map(({ id }) => variantIndex.get(id))
      .filter(Boolean)
      .map((variant) => {
        const storeNames = variant.storeIds
          .map((storeId) => storesById.get(storeId)?.name)
          .filter(Boolean);

        return toRow(variant, storeNames);
      });
  }

  const rowsByVariantId = new Map();

  selectedStores.forEach((store) => {
    const variant = variantByStoreId.get(store.id) || variantIndex.get(mainRecipeOptionId);

    if (!rowsByVariantId.has(variant.id)) {
      rowsByVariantId.set(variant.id, toRow(variant, []));
    }

    rowsByVariantId.get(variant.id).storeNames.push(store.name);
  });

  return Array.from(rowsByVariantId.values()).map((row) => ({
    ...row,
    storeNames: sortBy(row.storeNames),
  }));
};
